import { AdminPageHeader } from "@/components/admin-page-header";
import {
	getGetOpportunityByIdQueryKey,
	getGetOpportunityByIdQueryOptions,
	getGetOpportunitySharesQueryKey,
} from "@fp/admin-api-client/client";
import { listTalentMembers, type TalentMemberDto } from "@fp/admin-api-client";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import { OpportunityInvite } from "@fp/ui/components/opportunities/opportunity-invite";
import { useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	useRouter,
	useLocation,
} from "@tanstack/react-router";
import { toast } from "sonner";

export const Route = createFileRoute("/opps/$oppId/edit/invite")({
	loader: async ({ params, context: { queryClient } }) => {
		const [opportunity, talentMembersResponse] = await Promise.all([
			queryClient.fetchQuery(getGetOpportunityByIdQueryOptions(params.oppId)),
			queryClient.fetchQuery({
				queryKey: ["talentMembers"],
				queryFn: async () => listTalentMembers({ page: 1, pageSize: 1000 }),
			}),
		]);
		return {
			opportunity: opportunity.data,
			talentMembers: talentMembersResponse.data.items || ([] as TalentMemberDto[]),
		};
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { opportunity, talentMembers } = Route.useLoaderData();
	const queryClient = useQueryClient();

	const router = useRouter();
	const params = Route.useParams();
	const location = useLocation();

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Roster"
				parent={{
					url: router.buildLocation({
						to: "/opps/$oppId/edit/detail",
						params: { oppId: params.oppId },
					}).href,
					label: "Opp Preview",
				}}
			/>
			<PageContentContainer className="space-y-6 bg-white p-0">
				<OpportunityInvite
					opportunity={opportunity}
					talentMembers={talentMembers}
					onNewTalentMember={() => {
						router.navigate({ to: "/roster/add" });
					}}
					onTalentMemberUpdate={async () => {
						await queryClient.invalidateQueries({ queryKey: ["talentMembers"] });
						await router.invalidate();
					}}
					onInviteUpdate={async (invitedCount) => {
						await queryClient.invalidateQueries({
							queryKey: getGetOpportunityByIdQueryKey(params.oppId),
						});
						await queryClient.invalidateQueries({
							queryKey: getGetOpportunitySharesQueryKey(params.oppId),
						});
						await router.invalidate();
						toast.success(
							`Added ${invitedCount} talent ${invitedCount === 1 ? "member" : "members"} to "${opportunity.title}"`,
						);
						if (location.state.returnTo) {
							router.navigate({ to: location.state.returnTo });
						} else {
							router.navigate({ to: "/opps" });
						}
					}}
				/>
			</PageContentContainer>
		</AdminLayout>
	);
}
